import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Library, Upload, X } from "lucide-react";
import { CommunityImagePicker } from "./community-image-picker";

export type ImageSelectionResult =
  | { type: "upload"; file: File; preview: string }
  | { type: "community"; url: string; label: string };

interface ImageSelectorProps {
  onImageSelected: (result: ImageSelectionResult | null) => void;
  currentImageUrl?: string | null;
  currentImageLabel?: string | null;
  allowedCategories?: string[];
  initialSearch?: string;
  className?: string;
}

export function ImageSelector({
  onImageSelected,
  currentImageUrl,
  currentImageLabel,
  allowedCategories,
  initialSearch,
  className,
}: ImageSelectorProps) {
  const [showPicker, setShowPicker] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onImageSelected({
        type: "upload",
        file,
        preview: ev.target?.result as string,
      });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleClear = () => {
    onImageSelected(null);
  };

  const isCommunityImage = currentImageUrl?.includes("/community/");

  return (
    <div className={className}>
      {currentImageUrl && (
        <div className="relative w-full h-32 rounded-md overflow-hidden border bg-white flex items-center justify-center mb-2">
          <img
            src={currentImageUrl}
            alt={currentImageLabel || "Selected image"}
            className={isCommunityImage ? "w-20 h-20 object-contain" : "w-full h-full object-cover"}
          />
          {currentImageLabel && (
            <span className="absolute bottom-1 left-2 text-xs text-muted-foreground bg-white/80 px-1 rounded">
              {currentImageLabel}
            </span>
          )}
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => setShowPicker(true)}
        >
          <Library className="h-4 w-4 mr-1" />
          Image Library
        </Button>
        <label className="flex-1 cursor-pointer">
          <div className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3 w-full gap-1">
            <Upload className="h-4 w-4" />
            Upload Image
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
      </div>
      <CommunityImagePicker
        open={showPicker}
        onOpenChange={setShowPicker}
        onSelect={(image) => {
          onImageSelected({
            type: "community",
            url: image.url,
            label: image.label,
          });
        }}
        initialSearch={initialSearch}
        allowedCategories={allowedCategories}
      />
    </div>
  );
}
